import { Checkbox, Group, Loader, Radio, Select, Stack } from "@mantine/core";
import { DeviceTypes } from "../enums/DeviceTypes";
import { UserFilter } from "../models/UserFilter";
import { useEffect, useState } from "react";
import { fetchWithAuthentication } from "../utilities/FetchUtil";

interface UserFilterFormProps {
  filters: UserFilter;
  setFilters: (newFilter: UserFilter) => void;
}

export const UserFilterForm = ({
  filters,
  setFilters
}: UserFilterFormProps) => {
  const [versionsLoading, setVersionsLoading] = useState<boolean>(false);
  const [versionOptions, setVersionOptions] = useState<string[]>([]);
  const [appNamesLoading, setAppNamesLoading] = useState<boolean>(false);
  const [appNameOptions, setAppNameOptions] = useState<string[]>([]);

  const appVersionSelectOptions = ["All Versions", ...versionOptions];
  const appNameSelectOptions = ["All Apps", ...appNameOptions];

  const fetchAppNames = async () => {
    setAppNamesLoading(true);
    const response = await fetchWithAuthentication(
      `/apps`,
      "GET",
      null,
    );
    if (response.ok) {
      const json = await response.json();
      setAppNameOptions(json.appNames);
      setAppNamesLoading(false);
    } else {
      console.error("Error occurred while trying to fetch app names.");
      setAppNamesLoading(false);
    }
  };

  const fetchVersions = async () => {
    setVersionsLoading(true);
    const response = await fetchWithAuthentication(
      `/versions/${filters.appName ?? 'InterviewCopilot'}/${filters.deviceType}`,
      "GET",
      null,
    );
    if (response.ok) {
      const json = await response.json();
      setVersionOptions(json.appVersions);
      setVersionsLoading(false);
    } else {
      console.error("Error occurred while trying to fetch app versions.");
      setVersionsLoading(false);
    }
  };

  const handleDeviceVersionChange = (value: string | null) => {
    setFilters({
      ...filters,
      deviceVersion: value === "All Versions" ? null : value,
    });
  };

  const handleAppNameChange = (value: string | null) => {
    setFilters({
      ...filters,
      appName: value === "All Apps" ? null : value,
    });
  };

  useEffect(() => {
    fetchVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.deviceType, filters.appName]);

  useEffect(() => {
    fetchAppNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack align="center">
      <Group>
        <Radio
          label="IOS"
          checked={filters.deviceType === DeviceTypes.IOS}
          onChange={() =>
            setFilters({ ...filters, deviceType: DeviceTypes.IOS })
          }
        />
        <Radio
          label="ANDROID"
          type="radio"
          checked={filters.deviceType === DeviceTypes.ANDROID}
          onChange={() =>
            setFilters({
              ...filters,
              deviceType: DeviceTypes.ANDROID,
            })
          }
        />
      </Group>
      <Group>
        <Select
          styles={{ root: { display: "block" } }}
          label="App Name:"
          disabled={appNamesLoading}
          value={filters.appName ?? "All Apps"}
          onChange={(value) => handleAppNameChange(value)}
          data={appNameSelectOptions}
        />
        {versionsLoading && <Loader />}
      </Group>
      <Group>
        <Select
          styles={{ root: { display: "block" } }}
          label="App Version:"
          disabled={versionsLoading}
          value={filters.deviceVersion ?? "All Versions"}
          onChange={(value) => handleDeviceVersionChange(value)}
          data={appVersionSelectOptions}
        />
        {versionsLoading && <Loader />}
      </Group>
      <Stack>
        <Checkbox
          label="Email Required"
          checked={filters.emailRequired}
          onChange={() =>
            setFilters({
              ...filters,
              emailRequired: !filters.emailRequired,
            })
          }
        />

        <Checkbox
          label="Interviews Required"
          checked={filters.interviewsRequired}
          onChange={() =>
            setFilters({
              ...filters,
              interviewsRequired: !filters.interviewsRequired,
            })
          }
        />
      </Stack>
    </Stack>
  );
};
